import { Stack } from '@mui/material';
import { Field, useFormikContext } from 'formik';
import { ContractPicker } from 'entities/contract/contract-picker';
import { DatePickerField } from 'shared/ui/formik-fields/date-picker';
import { LegalEntityPicker } from 'entities/legal-entities/legal-entity-picker';
import { FormData, formTools } from './client-invoice.form-tools';
import { UserPickerField } from 'features/user/user-picker/ui';
import { FormDialog } from 'shared/ui/form-dialog';
import { CONTRACT_TYPE_CLIENT, ContractInfoDto } from '@sr/dto';
import { EntityRoles } from 'entities/invoice/types';
import { useStateSelector } from 'shared/store';
import { useBankDetailsList } from 'hooks/bank-details.hook';
import { BankDetailsPicker } from 'shared/ui/formik-fields/bank-details-picker';
import { useMemo, useState } from 'react';
import { ExternalPickerContainer } from 'components/Mui/external-picker-container';
import { BankDetailsDialog } from 'components/Companies/BankDetails/bank-details-dialog';
import { FormData as BankDetailsFormData } from 'components/Companies/BankDetails/bank-details.form';

type Props = {
  open: boolean;
  clientCompanyId: number;
  ourCompanyId: number;
  isClientSupplier?: boolean;
  onSubmit: (data: FormData) => Promise<void>;
  onClose: () => void;
};

export const AddClientInvoiceDialog = (props: Props) => {
  const { open, onSubmit, onClose, clientCompanyId, ourCompanyId, isClientSupplier = false } = props;

  const currentUser = useStateSelector((store) => store.auth.user);

  return (
    <FormDialog
      renderForm={(form) => <FormFields form={form} clientCompanyId={clientCompanyId} ourCompanyId={ourCompanyId} isClientSupplier={isClientSupplier} />}
      renderTitle="Новый счет"
      open={open}
      onClose={onClose}
      onSubmit={onSubmit}
      validate={formTools.validate}
      initialValues={{ ...formTools.emptyValues(), managerId: currentUser?.id ?? null }}
      dialogProps={{ maxWidth: 'sm', fullWidth: true }}
      validateOnChange={true}
    />
  );
};

const FormFields = ({ clientCompanyId, ourCompanyId, form, isClientSupplier }: { clientCompanyId: number; ourCompanyId: number; form: any; isClientSupplier: boolean }) => {
  const { values } = useFormikContext<FormData>();
  const sellerLegalEntityId = useMemo(() => (values?.sellerLegalEntityId ? Number(values.sellerLegalEntityId) : NaN), [values?.sellerLegalEntityId]);
  const buyerLegalEntityId = useMemo(() => (values?.buyerLegalEntityId ? Number(values.buyerLegalEntityId) : NaN), [values?.buyerLegalEntityId]);
  const sellerBankDetails = useBankDetailsList(isNaN(sellerLegalEntityId) ? undefined : sellerLegalEntityId);
  const buyerBankDetails = useBankDetailsList(isNaN(buyerLegalEntityId) ? undefined : buyerLegalEntityId);
  const [openSellerBankDetailsDialog, setOpenSellerBankDetailsDialog] = useState(false);
  const [openBuyerBankDetailsDialog, setOpenBuyerBankDetailsDialog] = useState(false);

  const { setFieldValue } = form;
  const handleContractChange = async (newContract: ContractInfoDto) => {
    if (!newContract?.buyer || !newContract?.seller) return;
    await setFieldValue('buyerLegalEntityId', newContract.buyer.id);
    await setFieldValue('buyerBankDetailsId', null);
    await setFieldValue('sellerLegalEntityId', newContract.seller.id);
    await setFieldValue('sellerBankDetailsId', null);
  };

  const handleAddSellerBankDetails = async (formData: BankDetailsFormData) => {
    const created = await sellerBankDetails.addBankDetails(formData);
    await setFieldValue('sellerBankDetailsId', created.id);
    setOpenSellerBankDetailsDialog(false);
  };

  const handleAddBuyerBankDetails = async (formData: BankDetailsFormData) => {
    const created = await buyerBankDetails.addBankDetails(formData);
    await setFieldValue('buyerBankDetailsId', created.id);
    setOpenBuyerBankDetailsDialog(false);
  };

  return (
    <Stack direction="column" spacing={2} sx={{ mt: 2 }}>
      <Stack direction="row" spacing={2}>
        <Field name="startDate" fullWidth label="Дата выставления" component={DatePickerField} required />
        <Field name="endDate" fullWidth label="Дата окончания" component={DatePickerField} required />
      </Stack>
      <Field name="contractId" label="Договор" component={ContractPicker} clientCompanyId={clientCompanyId} contractType={CONTRACT_TYPE_CLIENT} onChange={handleContractChange} required />
      <Field
        disabled
        label="Наше юрлицо"
        component={LegalEntityPicker}
        name="sellerLegalEntityId"
        companyIdFilter={ourCompanyId}
        entityRole={EntityRoles.INVOICE_ISSUER}
        required
      />
      <ExternalPickerContainer disabled={isNaN(sellerLegalEntityId)} onClick={() => setOpenSellerBankDetailsDialog(true)}>
        <Field
          label="Реквизиты продавца"
          component={BankDetailsPicker}
          disableBlockedForOutgoing
          name="sellerBankDetailsId"
          legalEntityId={sellerLegalEntityId}
          collection={sellerBankDetails.collection}
        />
      </ExternalPickerContainer>
      <Field disabled label="Юрлицо клиента" component={LegalEntityPicker} name="buyerLegalEntityId" companyIdFilter={clientCompanyId} required />
      <ExternalPickerContainer disabled={isNaN(buyerLegalEntityId)} onClick={() => setOpenBuyerBankDetailsDialog(true)}>
        <Field
          label="Реквизиты клиента"
          component={BankDetailsPicker}
          name="buyerBankDetailsId"
          legalEntityId={buyerLegalEntityId}
          collection={buyerBankDetails.collection}
        />
      </ExternalPickerContainer>
      <Field isLinkedToCompany isClientSupplier={isClientSupplier} includeUser name="managerId" label="Менеджер" component={UserPickerField} required />
      <BankDetailsDialog
        open={openSellerBankDetailsDialog}
        title="Добавить банковские реквизиты"
        onClose={() => setOpenSellerBankDetailsDialog(false)}
        submit={handleAddSellerBankDetails}
      />
      <BankDetailsDialog
        open={openBuyerBankDetailsDialog}
        title="Добавить банковские реквизиты"
        onClose={() => setOpenBuyerBankDetailsDialog(false)}
        submit={handleAddBuyerBankDetails}
      />
    </Stack>
  );
};
